import { progressFor, type InspectionPurge, type V2Inspection } from '../domain/inspection';
import { inspectionServiceOf } from '../domain/inspectionService';
import type { RecordWrite, WorkingDataKeys } from '../persistence/v2Database';
import { aiJobsStore, removeAiJobsOfInspectionLocal } from './aiJobsStore';
import { aiStore, removeAiOfInspectionLocal } from './aiStore';
import { evidenceStore, removeEvidenceOfInspectionLocal } from './evidenceStore';
import { findingStore, removeFindingsOfInspectionLocal } from './findingStore';
import { areasOf, inspectionStore, prepareInspectionPurge } from './inspectionStore';
import { releaseMediaUrls } from './mediaUrls';

// OD-12 (Owner ruling 2026-09-09, approved draft §5.3): generating the final report DELETES the
// working data of that inspection from the app and keeps only the PDFs and one basic-information
// row. This module prepares that deletion for the ONE transaction that stores the last PDF
// (src/state/reportStore.ts is the only caller), and applies it to the in-memory stores afterwards.
//
// What goes: areas, checklist rows, findings, photo records AND their bytes in the app's own media
// store, AI runs and AI observations.
// What stays: the final report PDFs, the signed agreement PDF, and the inspection record reduced to
// address · inspection date · client · PDF count · generation date · the frozen counts
// (src/domain/inspection.ts purgedInspection).
// What is NEVER touched: the device's own photo library. No code path in this app can delete from
// it — the app deletes its own copy only (OWNER-CONTRACT §6.1.2).
export interface PreparedPurge {
  purge: InspectionPurge;
  write: RecordWrite | null;
  keys: WorkingDataKeys;
  apply: () => void;
}

export function prepareWorkingDataPurge(inspection: V2Inspection, options: { pdfCount: number; generatedAt: number; now: number }): PreparedPurge {
  const inspectionId = inspection.id;
  const state = inspectionStore.get();
  const areas = areasOf(state, inspectionId);
  const enabledAreaIds = new Set(areas.filter((a) => a.enabled).map((a) => a.id));
  const items = state.items.filter((i) => i.inspectionId === inspectionId);
  const findings = findingStore.get().findings.filter((f) => f.inspectionId === inspectionId);
  const evidenceIds = evidenceStore.get().evidence.filter((e) => e.inspectionId === inspectionId).map((e) => e.id);
  const ai = aiStore.get();

  const purge: InspectionPurge = {
    purgedAt: options.now,
    generatedAt: options.generatedAt,
    pdfCount: options.pdfCount,
    service: inspectionServiceOf(inspection.property),
    progress: progressFor(items.filter((i) => enabledAreaIds.has(i.areaId))),
    areas: { enabledAreas: areas.filter((a) => a.enabled).length, completedAreas: areas.filter((a) => a.enabled && a.completedAt !== null).length },
    findingCount: findings.filter((f) => f.status === 'confirmed').length
  };

  const prepared = prepareInspectionPurge(inspectionId, purge, options.now);
  const keys: WorkingDataKeys = {
    areaIds: prepared.areaIds,
    itemIds: prepared.itemIds,
    evidenceIds,
    findingIds: findings.map((f) => f.id),
    aiRunIds: ai.runs.filter((r) => r.inspectionId === inspectionId).map((r) => r.id),
    aiObservationIds: ai.observations.filter((s) => s.inspectionId === inspectionId).map((s) => s.id),
    aiJobIds: aiJobsStore.get().jobs.filter((j) => j.inspectionId === inspectionId).map((j) => j.id)
  };

  return {
    purge,
    write: prepared.write,
    keys,
    apply: () => {
      prepared.apply();
      removeFindingsOfInspectionLocal(inspectionId);
      removeEvidenceOfInspectionLocal(inspectionId);
      removeAiOfInspectionLocal(inspectionId);
      removeAiJobsOfInspectionLocal(inspectionId);
      releaseMediaUrls(evidenceIds);
    }
  };
}
